import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchCommands} from '@/entities/device/api';
import type {DeviceCommandRecord} from '@/entities/device/types';
import {formatTimestamp} from '@/shared/lib/format';
import {Card} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {ListRow, PagedRows} from '@/shared/ui/list-row';
import {Skeleton} from '@/shared/ui/skeleton';
import {StatusPill, type StatusTone} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {radii, spacing} from '@/shared/ui/theme';

/**
 * What was asked of this terminal, and what came back.
 *
 * A remote command is not synchronous: the cabinet queues it and the terminal executes
 * it on its NEXT sync, up to a poll interval later, then acks. So "я нажал открыть, и
 * ничего не произошло" is usually "the command is `pending` and the device has not
 * called home yet" — a state the control panel above cannot show, because it only knows
 * that the queue call returned 200. This list is where that question is answered.
 *
 * Five states come off the wire (`pending` → `delivered` → `acked`, or `failed`, or
 * `expired` for a command no terminal picked up within a day), and
 * they are coloured by the app's usual rule: amber for in-flight, teal for done, red
 * for refused.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(2)},
});

/** The lifecycle states sv_control stamps, mapped to the palette's status system. */
function toneFor(status: string): StatusTone {
  switch (status) {
    case 'acked':
      return 'granted';
    case 'failed':
    case 'expired':
      return 'denied';
    case 'delivered':
      return 'online';
    default:
      return 'pending';
  }
}

/** The most recent commands are the ones anyone is asking about. */
const LIMIT = 12;

export function CommandHistory({
  companyId,
  deviceId,
}: {
  companyId: string;
  deviceId: string;
}) {
  const {t} = useTranslation();

  const query = useQuery({
    queryKey: ['device-commands', companyId, deviceId],
    queryFn: () => fetchCommands(companyId, deviceId),
    // A queued command changes state on the device's schedule, not on ours; a short
    // stale time means reopening this section actually re-asks.
    staleTime: 10_000,
  });

  if (query.isPending) {
    return <Skeleton height={120} radius={radii.lg} />;
  }

  if (query.isError) {
    return (
      <Card padding={4}>
        <AppText variant="caption">{t('errors.generic')}</AppText>
      </Card>
    );
  }

  const commands: DeviceCommandRecord[] = (query.data?.commands ?? []).slice(0, LIMIT);

  if (commands.length === 0) {
    return <EmptyState title={t('commands.empty')} hint={t('commands.emptyHint')} />;
  }

  return (
    <View style={styles.root}>
      <PagedRows
        items={commands}
        keyOf={command => command.id}
        testID="command-history"
        render={command => (
          <ListRow
            icon="send"
            title={t(`commands.kind.${command.kind}`, command.kind)}
            subtitle={
              command.result_message ||
              t('commands.queuedAt', {at: formatTimestamp(command.created_at)})
            }
            trailing={
              <StatusPill
                tone={toneFor(command.status)}
                size="sm"
                label={t(`commands.status.${command.status}`, command.status)}
              />
            }
            testID={`command-${command.id}`}
          />
        )}
      />
    </View>
  );
}
